"use client";

import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase";
import { PaymentFlow } from "./PaymentFlow";
import {
  Coffee,
  Heart,
  Wallet,
  ArrowLeft,
  X,
  ShieldOff,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

type PaymentPageType = "request" | "buymeCoffee" | "donation";

interface PublicPaymentPageProps {
  type: PaymentPageType;
  useSlug?: boolean;
}

interface PageCustomization {
  title: string;
  description: string;
  images: string[];
  backgroundImage: string | null;
  primaryColor: string;
  layout: "classic" | "modern" | "minimal";
  imageLayout?: "grid" | "carousel";
  thankYouMessage: string;
  buttonText?: string;
  minAmount?: number;
  maxAmount?: number;
}

interface UserData {
  wallet: string;
  smartWallet: string;
  username: string;
  photoURL?: string;
  email?: string;
  collectSettings?: {
    requestPaymentEnabled?: boolean;
    buyMeCoffeeEnabled?: boolean;
    donationBoxEnabled?: boolean;
    invoiceEnabled?: boolean;
    paymentTheme?: "dark" | "light";
  };
  donationBox?: PageCustomization;
  paymentLinkPage?: PageCustomization;
  coffeePage?: PageCustomization;
  disabledMethodsByType?: Record<string, string[]>;
}

function ImageCarousel({
  images,
  onImageClick,
}: {
  images: string[];
  onImageClick: (img: string) => void;
}) {
  const [current, setCurrent] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setCurrent((prev) => (prev + 1) % images.length);
    }, 4000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [images.length]);

  const goTo = (idx: number) => {
    setCurrent(idx);
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setCurrent((prev) => (prev + 1) % images.length);
    }, 4000);
  };

  return (
    <div>
      <div className="relative aspect-[16/9] rounded-2xl overflow-hidden border border-gray-700/30 group">
        {images.map((img, idx) => (
          <img
            key={idx}
            src={img}
            alt={`Gallery ${idx + 1}`}
            onClick={() => onImageClick(img)}
            className={`absolute inset-0 w-full h-full object-cover cursor-pointer transition-opacity duration-700 ${
              idx === current ? "opacity-100" : "opacity-0"
            }`}
          />
        ))}
        <button
          onClick={() => goTo((current - 1 + images.length) % images.length)}
          className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 bg-black/50 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70"
        >
          <ChevronLeft size={20} />
        </button>
        <button
          onClick={() => goTo((current + 1) % images.length)}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 bg-black/50 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70"
        >
          <ChevronRight size={20} />
        </button>
      </div>
      <div className="flex justify-center gap-2 mt-3">
        {images.map((_, idx) => (
          <button
            key={idx}
            onClick={() => goTo(idx)}
            className={`h-1.5 rounded-full transition-all ${
              idx === current ? "w-6 bg-white" : "w-1.5 bg-gray-600"
            }`}
          />
        ))}
      </div>
    </div>
  );
}

export default function PublicPaymentPage({
  type,
  useSlug = false,
}: PublicPaymentPageProps) {
  const params = useParams();
  const [userData, setUserData] = useState<UserData | null>(null);
  const [resolvedWallet, setResolvedWallet] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);

  useEffect(() => {
    const fetchUser = async () => {
      setLoading(true);
      setError(null);

      try {
        let walletAddress = "";

        if (useSlug && params.slug) {
          // Resolve slug to wallet address
          const slugDoc = await getDoc(
            doc(db, "slugs", params.slug.toLowerCase()),
          );
          if (!slugDoc.exists()) {
            setError("This payment link does not exist.");
            setLoading(false);
            return;
          }
          walletAddress = slugDoc.data().walletAddress?.toLowerCase() || "";
        } else if (params.walletAddress) {
          walletAddress = params.walletAddress.toLowerCase();
        } else {
          setError("Invalid payment link.");
          setLoading(false);
          return;
        }

        setResolvedWallet(walletAddress);

        // Fetch user data
        const userDoc = await getDoc(doc(db, "users", walletAddress));
        if (!userDoc.exists()) {
          // Try searching by smartWallet field
          const q = query(
            collection(db, "users"),
            where("smartWallet", "==", walletAddress),
          );
          const snap = await getDocs(q);
          if (snap.empty) {
            setError("User not found. This payment link may be invalid.");
            setLoading(false);
            return;
          }
          const data = snap.docs[0].data() as UserData;
          setUserData(data);
          setResolvedWallet(data.smartWallet || data.wallet);
        } else {
          setUserData(userDoc.data() as UserData);
        }
        // Log page view
        addDoc(collection(db, "users", walletAddress, "pageViews"), {
          type,
          timestamp: serverTimestamp(),
        }).catch(() => {});
      } catch (err) {
        console.error("Error fetching user:", err);
        setError("Failed to load payment page. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [params.walletAddress, params.slug, useSlug]);

  if (loading) {
    return <div className="min-h-screen bg-gradient-to-br from-[#090D14] via-[#0A0E15] to-[#0B0F16]" />;
  }

  if (error || !userData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#090D14] via-[#0A0E15] to-[#0B0F16] p-4">
        <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-500/10 flex items-center justify-center">
            <Wallet className="w-8 h-8 text-red-400" />
          </div>
          <h2 className="text-xl font-bold text-white mb-3">Page Not Found</h2>
          <p className="text-gray-400 mb-6">
            {error || "This payment link is invalid."}
          </p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-[#61CE70] hover:text-[#72D981] font-medium transition-colors"
          >
            <ArrowLeft size={18} />
            Go to CashTap
          </Link>
        </div>
      </div>
    );
  }

  // Check if this payment type is disabled by the user
  const settings = userData.collectSettings;
  const isDisabled =
    (type === "request" && settings?.requestPaymentEnabled === false) ||
    (type === "buymeCoffee" && settings?.buyMeCoffeeEnabled === false) ||
    (type === "donation" && settings?.donationBoxEnabled === false);

  if (isDisabled) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#090D14] via-[#0A0E15] to-[#0B0F16] p-4">
        <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-orange-500/10 flex items-center justify-center">
            <ShieldOff className="w-8 h-8 text-orange-400" />
          </div>
          <h2 className="text-xl font-bold text-white mb-3">
            Payments Not Available
          </h2>
          <p className="text-gray-400 mb-6">
            This user has disabled this payment method. Please contact them
            directly for alternative ways to send payment.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[#61CE70] hover:text-[#72D981] font-medium transition-colors"
          >
            <ArrowLeft size={18} />
            Go to CashTap
          </Link>
        </div>
      </div>
    );
  }

  // Get the customization config for the current page type
  const getPageCustomization = (): PageCustomization | undefined => {
    switch (type) {
      case "donation":
        return userData.donationBox;
      case "request":
        return userData.paymentLinkPage;
      case "buymeCoffee":
        return userData.coffeePage;
    }
  };

  const pageConfig = getPageCustomization();
  const pageTheme = userData.collectSettings?.paymentTheme || "dark";
  const bgStyle =
    pageConfig?.backgroundImage && pageTheme === "dark"
      ? {
          backgroundImage: `linear-gradient(to bottom, rgba(9,13,20,0.85), rgba(9,13,20,0.95)), url(${pageConfig.backgroundImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }
      : {};

  const getPageConfig = () => {
    const defaultIcons = {
      request: <Wallet className="w-8 h-8 text-[#61CE70]" />,
      buymeCoffee: <Coffee className="w-8 h-8 text-yellow-400" />,
      donation: <Heart className="w-8 h-8 text-pink-400" />,
    };
    const defaultHeadings = {
      request: `Pay ${userData.username || "User"}`,
      buymeCoffee: `Buy ${userData.username || "User"} a Coffee`,
      donation: `Support ${userData.username || "User"}`,
    };
    const defaultSubtitles = {
      request: "Send a payment securely via CashTap",
      buymeCoffee: "Show your appreciation with a quick tip!",
      donation: "Your donation makes a difference",
    };
    const defaultColors = {
      request: "#61CE70",
      buymeCoffee: "#FBBF24",
      donation: "#EC4899",
    };

    return {
      icon: defaultIcons[type],
      heading: pageConfig?.title || defaultHeadings[type],
      subtitle: pageConfig?.description || defaultSubtitles[type],
      accentColor: pageConfig?.primaryColor || defaultColors[type],
    };
  };

  const config = getPageConfig();
  const theme = userData.collectSettings?.paymentTheme || "dark";
  const isLight = theme === "light";
  // The payment-link page is a single minimal card — no cover image, avatar
  // or gallery — so the buyer sees the form immediately without scrolling.
  const isMinimal = type === "request";

  if (isMinimal) {
    const initial = (userData.username || "U").charAt(0).toUpperCase();
    return (
      <div
        className={`min-h-dvh flex flex-col items-center justify-center px-3 sm:px-4 py-3 ${isLight ? "bg-gray-50" : "bg-gradient-to-br from-[#090D14] via-[#0A0E15] to-[#0B0F16]"}`}
      >
        <div className="mt-0 w-full max-w-xl lg:max-w-none lg:w-[50vw]">
          {/* Single payment card */}
          <div
            className={`rounded-3xl overflow-hidden shadow-2xl border ${isLight ? "border-gray-200" : "border-gray-700/50"}`}
          >
            {/* Green header with vendor avatar */}
            <div
              className="px-4 sm:px-5 py-2.5 sm:py-3 flex items-center gap-3"
              style={{
                background: `linear-gradient(135deg, ${config.accentColor}, ${config.accentColor}cc)`,
              }}
            >
              <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-full overflow-hidden bg-white/20 ring-2 ring-white/40 flex items-center justify-center shrink-0">
                {userData.photoURL ? (
                  <img
                    src={userData.photoURL}
                    alt={userData.username}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-white font-bold text-base sm:text-lg">{initial}</span>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <h1 className="text-white font-bold text-sm sm:text-lg leading-tight truncate">
                  {config.heading}
                </h1>
                <p className="text-white/85 text-[11px] sm:text-[13px] leading-tight truncate">
                  {config.subtitle}
                </p>
              </div>
            </div>

            {/* Body — compact PaymentFlow */}
            <div className={`p-3 sm:px-6 sm:py-4 ${isLight ? "bg-white" : "bg-[#0F1419]"}`}>
              <PaymentFlow
                targetWalletAddress={resolvedWallet}
                recipientName={userData.username}
                recipientPhotoURL={userData.photoURL}
                isPublic={true}
                compact={true}
                showWalletDisplay={false}
                showManagePayments={false}
                minAmount={pageConfig?.minAmount}
                maxAmount={pageConfig?.maxAmount}
                title={config.heading}
                subtitle={config.subtitle}
                accentColor={config.accentColor}
                theme={theme}
                buttonText={pageConfig?.buttonText || "Top Up Now"}
                disabledPaymentMethods={
                  userData.disabledMethodsByType?.[type] || []
                }
                invoiceSource={type}
                invoiceCollectSlug={
                  useSlug ? (params.slug as string) : undefined
                }
              />
            </div>
          </div>

          {/* Footer */}
          <div className="mt-2.5 flex items-center justify-center gap-2 opacity-60">
            <img src="/logo.png" alt="CashTap" className="h-8 w-auto" />
            <span
              className={`text-[11px] ${isLight ? "text-gray-400" : "text-gray-500"}`}
            >
              Pay Securely With CashTap
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen ${isLight ? "bg-gray-50" : "bg-gradient-to-br from-[#090D14] via-[#0A0E15] to-[#0B0F16]"}`}
      style={isLight ? {} : bgStyle}
    >
      {/* Lightbox */}
      {lightboxImage && (
        <div
          className="fixed inset-0 z-[2000] bg-black/90 backdrop-blur-md flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
        >
          <button
            onClick={() => setLightboxImage(null)}
            className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors"
          >
            <X size={32} />
          </button>
          <img
            src={lightboxImage}
            alt="Gallery"
            className="max-w-full max-h-[90vh] object-contain rounded-2xl"
          />
        </div>
      )}

      {/* Header */}
      <header
        className={`w-full border-b ${isLight ? "border-gray-200 bg-white/80" : "border-gray-800/50 bg-[#090D14]/80"} backdrop-blur-lg`}
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-center">
          <Link to="/" className={`flex items-center gap-2.5 text-base ${isLight ? "text-gray-500" : "text-gray-400"}`}>
            <span className="font-medium">Powered by</span>
            <img src="/logo.png" alt="CashTap" className="h-12 scale-125 ml-3 w-auto" />
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-0 sm:py-0">
        {/* ── Facebook-profile-style hero ── */}
        {/* Cover / Background image */}
        <div className="relative -mx-4 sm:-mx-6 mb-14">
          {/* Cover image container */}
          <div className="rounded-b-3xl overflow-hidden">
            {pageConfig?.backgroundImage || (pageConfig?.images && pageConfig.images.length > 0) ? (
              <button
                onClick={() =>
                  setLightboxImage(
                    pageConfig.backgroundImage || pageConfig.images[0]
                  )
                }
                className="w-full relative block"
              >
                <img
                  src={pageConfig.backgroundImage || pageConfig.images[0]}
                  alt="Cover"
                  className="w-full h-48 sm:h-64 md:h-72 object-cover"
                />
                <div
                  className={`absolute inset-0 ${isLight ? "bg-gradient-to-t from-white via-white/40 to-transparent" : "bg-gradient-to-t from-[#090D14] via-[#090D14]/40 to-transparent"}`}
                />
              </button>
            ) : (
              <div
                className={`w-full h-48 sm:h-64 md:h-72 ${isLight ? "bg-gradient-to-br from-gray-100 to-gray-200" : "bg-gradient-to-br from-[#0F1419] to-[#1A1F2B]"}`}
              />
            )}
          </div>

          {/* Logo / Avatar — bottom center, overlapping outside the overflow container */}
          <div className="absolute -bottom-12 left-1/2 -translate-x-1/2 z-10">
            <div
              className={`w-24 h-24 rounded-full overflow-hidden border-4 ${isLight ? "border-white bg-white" : "border-[#090D14] bg-[#090D14]"} shadow-xl`}
            >
              {userData.photoURL ? (
                <img
                  src={userData.photoURL}
                  alt={userData.username}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div
                  className="w-full h-full flex items-center justify-center"
                  style={{ background: `linear-gradient(135deg, ${config.accentColor}33, ${config.accentColor}11)` }}
                >
                  {config.icon}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Title & Subtitle — centered below avatar */}
        <div className="text-center mb-6">
          <h1
            className={`text-2xl sm:text-3xl font-bold ${isLight ? "text-gray-900" : "text-white"} mb-1.5`}
          >
            {config.heading}
          </h1>
          <p
            className={`${isLight ? "text-gray-500" : "text-gray-400"} text-sm sm:text-base max-w-lg mx-auto`}
          >
            {config.subtitle}
          </p>
        </div>

       
        {/* Gallery — remaining images (skip first if used as cover) */}
        {pageConfig?.images && pageConfig.images.length > (pageConfig.backgroundImage ? 0 : 1) && (
          <div
            className={`${isLight ? "bg-white border border-gray-200 shadow-sm" : "bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50"} rounded-3xl p-5 sm:p-6 mb-8`}
          >
            {(() => {
              const galleryImages = pageConfig.backgroundImage
                ? pageConfig.images
                : pageConfig.images.slice(1);
              return pageConfig.imageLayout === "carousel" &&
                galleryImages.length > 1 ? (
                <ImageCarousel
                  images={galleryImages}
                  onImageClick={setLightboxImage}
                />
              ) : (
                <div
                  className={`grid gap-3 ${galleryImages.length === 1 ? "grid-cols-1" : galleryImages.length === 2 ? "grid-cols-2" : "grid-cols-2 sm:grid-cols-3"}`}
                >
                  {galleryImages.map((img: string, idx: number) => (
                    <button
                      key={idx}
                      onClick={() => setLightboxImage(img)}
                      className="relative group overflow-hidden rounded-2xl aspect-square border border-gray-700/30 hover:border-gray-600/50 transition-all"
                    >
                      <img
                        src={img}
                        alt={`Gallery ${idx + 1}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                    </button>
                  ))}
                </div>
              );
            })()}
          </div>
        )}

        {/* PaymentFlow — shared across all layouts */}
        <PaymentFlow
          targetWalletAddress={resolvedWallet}
          recipientName={userData.username}
          recipientPhotoURL={userData.photoURL}
          isPublic={true}
          showWalletDisplay={false}
          showManagePayments={false}
          presetAmounts={type === "buymeCoffee" ? [3, 5, 10, 20, 25, 30, 50, 75, 100] : type === "donation" ? [5, 10, 20, 50, 75, 100, 200, 300, 400, 500] : undefined}
          minAmount={type === "donation" ? 0.5 : undefined}
          maxAmount={type === "donation" ? 500 : undefined}
          amountPlaceholder={type === "donation" ? "Enter your donation from 0.5 to 500" : undefined}
          title={config.heading}
          subtitle={config.subtitle}
          accentColor={config.accentColor}
          theme={theme}
          buttonText={
            pageConfig?.buttonText ||
            (type === "donation"
              ? "Donate Now"
              : type === "buymeCoffee"
                ? "Send Tip"
                : "Send Payment")
          }
          disabledPaymentMethods={
            userData.disabledMethodsByType?.[
              type === "buymeCoffee" ? "coffee" : type
            ] || []
          }
          invoiceSource={type}
          invoiceCollectSlug={useSlug ? (params.slug as string) : undefined}
        />

        {/* Thank You Message */}
        {pageConfig?.thankYouMessage && (
          <div
            className={`mt-8 ${isLight ? "bg-white border border-gray-200 shadow-sm" : "bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50"} rounded-3xl p-6 text-center`}
          >
            <Heart
              className="w-8 h-8 mx-auto mb-3"
              style={{ color: config.accentColor }}
            />
            <p
              className={`${isLight ? "text-gray-600" : "text-gray-300"} italic`}
            >
              {pageConfig.thankYouMessage}
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 text-center space-y-3">
          <div className="flex items-center justify-center gap-2">
            <img
              src="/cashtap-logo.png"
              alt="CashTap"
              className="h-5 w-auto"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
            <span
              className={`${isLight ? "text-gray-400" : "text-gray-500"} text-xs`}
            >
              Secure payments powered by CashTap
            </span>
          </div>
        </div>
      </main>
    </div>
  );
}
